import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * One ticking clock for the whole console.
 *
 * A hundred device rows each showing "3 分鐘前" used to mean a hundred
 * `setInterval`s, a hundred timers the browser has to service and a hundred
 * separate re-renders that never line up. One interval, one context value, one
 * coordinated re-render.
 *
 * 15 s is chosen to match the coarsest thing it drives: relative times round to
 * minutes, and the online/stale boundary is five minutes wide.
 */
const TICK_MS = 15_000;

const Ctx = createContext<number>(Date.now());

export function ClockProvider({ children, tickMs = TICK_MS }: { children: ReactNode; tickMs?: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), tickMs);
    // A tab that was backgrounded for an hour must not render an hour-stale
    // clock for another 15 s.
    const onVisible = () => {
      if (document.visibilityState === "visible") setNow(Date.now());
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [tickMs]);

  return <Ctx.Provider value={now}>{children}</Ctx.Provider>;
}

/** Epoch ms, updated on the shared tick. */
export function useNow(): number {
  return useContext(Ctx);
}
